/**
 * The shell's localStorage inventory — every key the portal reads or writes, in one
 * place. Keys follow the frontend convention `medialib:<scope>:<name>` built with the
 * styleguide's {@link storageKey}; see the styleguide's `src/storage.ts` for the rule.
 *
 * The shell-owned keys read by pilets (`user`, `session`) are defined in the styleguide
 * (the only home a cross-package key can have) and re-exported here so this file stays
 * the portal's complete inventory.
 */
import {
  storageKey,
  migrateStorageKey,
  USER_STORAGE_KEY,
  SESSION_STORAGE_KEY,
  DEVICE_ID_STORAGE_KEY
} from '@medialib/styleguide'

// `device_id` is shell-written but read by the profile pilet's device list, so it
// lives in the styleguide with the other cross-package keys; re-exported here so this
// file stays the portal's complete inventory.
export { USER_STORAGE_KEY, SESSION_STORAGE_KEY, DEVICE_ID_STORAGE_KEY }

/** The active UI language, shared with every pilet via `use-persisted-state`. */
export const LANGUAGE_STORAGE_KEY = storageKey('portal', 'lang')

/** The cookie-consent decision (CMP). */
export const CONSENT_STORAGE_KEY = storageKey('portal', 'cookie_consent')

/**
 * Marks that this tab has already been sent to the sign-in app because the domain
 * is private — the loop guard in `auth/privateAccess.ts`.
 *
 * The one key here held in **sessionStorage**, not localStorage: it must live
 * exactly as long as the navigation sequence it protects. Persisted, it would
 * outlive that sequence and silently suppress a legitimate redirect days later.
 */
export const LOGIN_REDIRECT_STORAGE_KEY = storageKey('portal', 'login_redirect')

// Move the legacy un-namespaced portal keys to the convention once, before any hook
// reads them (this module is imported by the hook modules that own each key).
migrateStorageKey('lang', LANGUAGE_STORAGE_KEY)
migrateStorageKey('cookie-consent', CONSENT_STORAGE_KEY)
